
import {
    type ComponentRef,
    useCallback,
    useEffect,
    useState,
    useRef
} from "react";
import {
    ActivityIndicator,
    TouchableOpacity,
    RefreshControl,
    ScrollView,
    View
} from "react-native";
import type {
    HomeScreenNavigationProp
} from "./type";
import stylesheet from "./stylesheet";
import {
    menuItems
} from "./constants";
import {
    type IBottomSheetRef,
    NCoreUIKitLocalize,
    NCoreUIKitTheme,
    PageContainer,
    BottomSheet,
    TextInput,
    RowCard,
    Button,
    Text
} from "ncore-ui-kit";
import {
    useGlobalState
} from "../../../context";
import {
    useDebouncedDimensions,
    useWebScrollDrag,
    formatDate
} from "../../../utils";
import {
    useFocusEffect,
    useNavigation
} from "@react-navigation/native";
import type {
    AnnouncementData
} from "../../../services/restAPI/actions/getAnnouncements/types";
import type {
    CompaniesObjectType
} from "../../../services/restAPI/actions/getCompanies/types";
import type {
    FeaturedData
} from "../../../services/restAPI/actions/getFeaturedData/types";
import type {
    GetNewsResquestType,
    NewsData
} from "../../../services/restAPI/actions/getNews/types";
import type {
    NewsCategoriesObjectType
} from "../../../services/restAPI/actions/getNewsCategories/types";
import {
    ChevronRightIcon,
    NewspaperIcon,
    SearchIcon,
    MenuIcon,
    BellIcon
} from "lucide-react-native";
import Carousel, {
    Pagination
} from "react-native-snap-carousel";
import {
    AnnouncementCard,
    InstallAppBanner,
    FeaturedCard,
    CompanyCard,
    NewsCard,
    UserCard
} from "../../../components";
import {
    FooterVersionInfo
} from "../../../components";
import {
    RESTService
} from "../../../services/restAPI";
import {
    storage
} from "../../../storage";

const Home = () => {
    const {
        windowWidth
    } = useDebouncedDimensions();

    const navigation = useNavigation<HomeScreenNavigationProp>();
    const {
        radiuses,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    const [globalState,
        setGlobalState] = useGlobalState();

    const [
        selected,
        setSelected
    ] = useState<keyof NCoreUIKit.Translation>("latest" as keyof NCoreUIKit.Translation);

    const [
        featuredIndex,
        setFeaturedIndex
    ] = useState(0);

    const [
        searchText,
        setSearchText
    ] = useState("");

    const [
        featuredData,
        setFeaturedData
    ] = useState<FeaturedData[]>([]);

    const [
        announcementData,
        setAnnouncementData
    ] = useState<AnnouncementData[]>([]);

    const [
        companyData,
        setCompanyData
    ] = useState<Array<CompaniesObjectType>>([]);

    const [
        newsCategoriesData,
        setNewsCategoriesData
    ] = useState<Array<NewsCategoriesObjectType>>([]);

    const [
        newsData,
        setNewsData
    ] = useState<NewsData[]>([]);

    const [
        loading,
        setLoading
    ] = useState(true);

    const [
        refreshing,
        setRefreshing
    ] = useState(false);


    const [
        activeIndex,
        setActiveIndex
    ] = useState(0);

    const [
        unreadCount,
        setUnreadCount
    ] = useState<number>(0);

    const bottomSheetRef = useRef<IBottomSheetRef>(null);

    const carouselRef = useRef(null);

    const featuredListRef = useRef<ComponentRef<typeof ScrollView> | null>(null);

    const featuredInterval = useRef<ReturnType<typeof setInterval>>(null);

    const featuredIsUnderInteraction = useRef(false);

    const categoriesScrollRef = useRef<ComponentRef<typeof ScrollView> | null>(null);
    const companiesScrollRef = useRef<ComponentRef<typeof ScrollView> | null>(null);

    useWebScrollDrag(featuredListRef);
    useWebScrollDrag(carouselRef);
    useWebScrollDrag(categoriesScrollRef);
    useWebScrollDrag(companiesScrollRef);

    const featuredIndexRef = useRef(0);

    useEffect(() => {
        getMyProfile();
        getNewsCategories();
        getFeaturedData();
        getAnnouncements();
        getCompanies();
        getNews();

    }, []);



    useEffect(() => {
        getNews();
    }, [selected]);

    useEffect(() => {
        if (!featuredData || featuredData.length === 0) return;

        startFeaturedInterval();

        return () => {
            clearFeaturedInterval();
        };
    }, [featuredData]);

    useEffect(() => {
        featuredListRef.current?.scrollTo({
            x: featuredIndexRef.current * (windowWidth - (spaces.spacingMd * 2)),
            animated: true
        });
    }, [featuredIndex]);

    const startFeaturedInterval = () => {
        featuredInterval.current = setInterval(() => {
            setFeaturedIndex(() => {
                const nextIndex = featuredIndexRef.current + 1 >= featuredData.length - 1 ? 0 : featuredIndexRef.current + 1;

                featuredIndexRef.current = nextIndex;

                return nextIndex;
            });
        }, 6000);
    };

    const clearFeaturedInterval = () => {
        clearInterval(featuredInterval.current as ReturnType<typeof setInterval>);
        featuredInterval.current = null;
    };

    const getUnreadNotificationCount = useCallback(() => {
        if(globalState.isGuest) {
            return Promise.resolve();
        }

        return RESTService.action("GetUnreadCount", null)
            .then((res) => {
                if(res.code === 200 || res.code === 201 || res.code === 202) {
                    setUnreadCount(res.payload.unreadCount);
                }
            })
            .catch(() => {
            });
    }, [
        globalState.isGuest
    ]);

    useFocusEffect(
        useCallback(() => {
            getUnreadNotificationCount();
        }, [
            getUnreadNotificationCount
        ])
    );

    const getFeaturedData = () => {
        return RESTService.action("GetFeaturedData", {
            language: storage.getString("language") ?? "tr-TR"
        })
            .then((res) => {
                if(res.code === 200 || res.code === 201 || res.code === 202) {
                    setFeaturedData(res.payload);
                }
            }).catch((err) => {
                console.log(err);
            }).finally(() => {
                setLoading(false);
            });
    };

    const getAnnouncements = () => {
        return RESTService.action("GetAnnouncements", {
            language: storage.getString("language") ?? "tr-TR"
        })
            .then((res) => {
                if(res.code === 200 || res.code === 201 || res.code === 202) {
                    setAnnouncementData(res.payload);
                }
            }).catch((err) => {
                console.log(err);
            }).finally(() => {
                setLoading(false);
            });
    };

    const getCompanies = () => {
        return RESTService.action("GetCompanies", null)
            .then((res) => {
                if(res.code === 200 || res.code === 201 || res.code === 202) {
                    setCompanyData(res.payload);
                }
            }).catch((err) => {
                console.log(err);
            }).finally(() => {
                setLoading(false);
            });
    };

    const getNews = () => {
        const params: GetNewsResquestType = {
            language: storage.getString("language") ?? "tr-TR"
        };

        if(selected !== "latest") {
            params.categories= [selected];
        }

        return RESTService.action("GetNews", params)
            .then((res) => {
                if(res.code === 200 || res.code === 201 || res.code === 202) {
                    setNewsData(res.payload);
                } else {
                    setNewsData([]);
                }
            }).catch((err) => {
                console.log(err);
            }).finally(() => {
                setLoading(false);
            });
    };

    const getNewsCategories = () => {
        return RESTService.action("GetNewsCategories", null)
            .then((res) => {
                if(res.code === 200 || res.code === 201 || res.code === 202) {
                    setNewsCategoriesData([
                        {
                            _id: "static-latest",
                            key: "latest",
                            titleKey: "latest"
                        },
                        ...res.payload
                    ]);
                }
            }).catch((err) => {
                console.log(err);
            }).finally(() => {
                setLoading(false);
            });
    };

    const getMyProfile = () => {
        if (globalState.isGuest) return Promise.resolve();

        return RESTService.action("GetMyProfile", null)
            .then((res) => {
                if(res.code === 200 || res.code === 201 || res.code === 202) {
                    const companies = res.payload.companies;
                    const firstCompany = companies && companies.length > 0 ? companies[0] : null;
                    if(firstCompany && firstCompany.companyTitle && globalState.user) {
                        setGlobalState({
                            ...globalState,
                            user: {
                                ...globalState.user,
                                companyTitle: firstCompany.companyTitle,
                                companyName: firstCompany.companyTitle,
                                company: firstCompany.companyTitle
                            }
                        });
                    }
                }
            }).catch((err) => {
                console.log(err);
            });
    };

    const renderHeader = () => {
        return <View
            style={stylesheet.headerContent}
        >
            <UserCard
                fullName={globalState.isGuest ? localize("guest") : globalState.user?.fullName || ""}
                profilePhotoURL={globalState.user?.profilePhotoURL}
                company={globalState.user?.companyTitle || globalState.user?.companyName || globalState.user?.company || ""}
                onPress={() => {
                    navigation.navigate("Profile");
                }}
            />
            <View
                style={stylesheet.iconContent}
            >
                <TouchableOpacity
                    style={[
                        stylesheet.notificationButton,
                        {
                            marginRight: spaces.spacingMd
                        }
                    ]}
                    onPress={() => {
                        navigation.navigate("Notifications");
                    }}
                >
                    <BellIcon
                        size={25}
                    />
                    {unreadCount > 0 && <View
                        style={[
                            stylesheet.notificationBadge,
                            {
                                backgroundColor: colors.system.default.info,
                                paddingHorizontal: spaces.spacingXs,
                                borderRadius: radiuses.full
                            }
                        ]}
                    >
                        <Text
                            variant="labelSmallSize"
                            color="onPrimary"
                            style={stylesheet.badgeText}
                        >
                            {unreadCount > 9 ? "9+" : unreadCount}
                        </Text>
                    </View>}
                </TouchableOpacity>
                <TouchableOpacity
                    onPress={() => {
                        bottomSheetRef.current?.open();
                    }}
                >
                    <MenuIcon
                        size={25}
                    />
                </TouchableOpacity>
            </View>
        </View>;
    };

    const renderSearch = () => {
        return <View
            style={[
                stylesheet.searchContainer,
                {
                    paddingVertical: spaces.spacingMd,
                    paddingHorizontal: spaces.spacingMd
                }
            ]}
        >
            <View
                style={[
                    stylesheet.searchInputContainer,
                    {
                        marginRight: spaces.spacingMd
                    }
                ]}
            >
                <TextInput
                    placeholder={localize("search-name-company-industry")}
                    customBorderColor="rgba(233, 237, 240, 1)"
                    customColor="rgba(233, 237, 240, 1)"
                    spreadBehaviour="stretch"
                    onChangeText={(text) => {
                        setSearchText(text);
                    }}
                    onSubmitEditing={() => {
                        navigation.navigate("MainTabs", {
                            screen: "SearchTab",
                            params: {
                                searchTextParam: searchText
                            }
                        });
                    }}
                />
            </View>
            <Button
                spreadBehaviour="free"
                icon={() => <SearchIcon
                    size={20}
                    color={colors.project.black}
                />
                }
                onPress={() => {
                    navigation.navigate("MainTabs", {
                        screen: "SearchTab",
                        params: {
                            searchTextParam: searchText
                        }
                    });
                }}
            />
        </View>;
    };

    const renderItem = ({
        item,
        index
    }: { item: FeaturedData, index: number }) => {
        return <FeaturedCard
            onPress={() => {
                if(item.type === "announcement"){
                    navigation.navigate("AnnouncementDetail", {
                        announcementID: item._id
                    });
                }

                if (item.type === "news") {
                    navigation.navigate("NewsDetail", {
                        newsID: item._id
                    });
                }
            }}
            key={`featured-card-${index}`}
            _id={item._id}
            title={item.title}
            coverPhotoURL={item.coverPhotoURL}
            shortContent={item.shortContent}
            type={item.type}
            index={index}
            currentIndex={featuredIndex}
            totalCount={featuredData.length}
        />;
    };

    const renderItemAnnouncement = ({
        item,
        index
    }: { item: AnnouncementData, index: number}) => {
        return <AnnouncementCard
            title={item.title}
            onPress={() => {
                navigation.navigate("AnnouncementDetail", {
                    announcementID: item._id
                });
            }}
            key={item._id}
            disabled={index === activeIndex ? false :true}
            coverPhotoURL={item.coverPhotoURL}
            content={item.content}
        />;
    };

    const renderItemCompany = ({
        item
    }: { item: CompaniesObjectType}) => {
        return <CompanyCard
            key={item.companyID}
            companyLogo={item.companyLogo}
            onPress={() => {
                navigation.navigate("CompanyDetail", {
                    companyID: item.companyID
                });
            }}
        />;
    };

    const renderItemNews = ({
        item
    }: { item: NewsData}) => {
        let displayTitle = "";
        if (item.categoryIDs && item.categoryIDs.length > 0) {
            const titles = item.categoryIDs
                .map(id => newsCategoriesData.find(c => c._id === id)?.titleKey)
                .filter(Boolean)
                .map(key => localize(key as Parameters<typeof localize>[0]));

            displayTitle = titles.join(", ");
            if (displayTitle.length > 30) {
                displayTitle = `${displayTitle.substring(0, 27)}...`;
            }
        }

        return <NewsCard
            key={item._id}
            _id={item._id}
            createdAt= {formatDate(item.createdAt)}
            title= {item.title}
            shortContent={item.shortContent}
            coverPhotoURL={item.coverPhotoURL}
            onPress={() => {
                navigation.navigate("NewsDetail", {
                    newsID: item._id
                });
            }}
            type={displayTitle}
        />;
    };

    const renderFeatured = () => {
        return <ScrollView
            horizontal={true}
            style={stylesheet.featuredScrollView}
            ref={featuredListRef}
            key={`featured-${windowWidth}`}
            bounces={false}
            bouncesZoom={false}
            decelerationRate="fast"
            disableIntervalMomentum={true}
            overScrollMode="never"
            showsHorizontalScrollIndicator={false}
            snapToInterval={windowWidth - (spaces.spacingMd * 2)}
            snapToAlignment="center"
            pagingEnabled={true}
            onTouchStart={() => {
                featuredIsUnderInteraction.current = true;
                clearFeaturedInterval();
            }}
            onMomentumScrollEnd={(e) => {
                if(!featuredIsUnderInteraction.current) {
                    return;
                }

                featuredIsUnderInteraction.current = false;

                const newIndex = Math.round(e.nativeEvent.contentOffset.x / (windowWidth - (spaces.spacingMd * 2)));

                featuredIndexRef.current = newIndex;
                setFeaturedIndex(newIndex);

                setTimeout(() => {
                    if(!featuredInterval.current) startFeaturedInterval();
                }, 5000);
            }}
        >
            {featuredData && featuredData.length ? featuredData.map((featuredItem, featuredIndex) => {
                return renderItem({
                    item: featuredItem,
                    index: featuredIndex
                });
            }) : <View
                style={stylesheet.emptyContentContainer}
            >
                <Text
                    color="mid"
                    variant="bodyMediumSize"
                >
                    {localize("not-found")}
                </Text>
            </View>}
        </ScrollView>;
    };

    const renderAnnouncements = () => {
        return announcementData && announcementData.length ?
            <View
                style={[
                    stylesheet.fullWidthContainer,
                    {
                        width: windowWidth
                    }
                ]}
            >
                <View
                    style={[
                        stylesheet.sectionHeaderContainer,
                        {
                            paddingVertical: spaces.spacingMd,
                            paddingHorizontal: spaces.spacingMd
                        }
                    ]}
                >
                    <Text
                        color="high"
                        variant="titleLargeSize"
                    >
                        {localize("announcements")}
                    </Text>
                    <TouchableOpacity
                        onPress={() => {
                            navigation.navigate("Announcements");
                        }}
                        style={stylesheet.allButtonContainer}
                    >
                        <Text
                            variant="bodyMediumSize"
                            color="mid"
                        >
                            {localize("all")}
                        </Text>
                    </TouchableOpacity>
                </View>
                <Carousel
                    ref={carouselRef}
                    key={`carousel-${windowWidth}`}
                    data={announcementData}
                    renderItem={({
                        item,
                        index
                    }) => {
                        return <View
                            style={{
                                paddingRight: spaces.spacingMd
                            }}
                        >
                            {renderItemAnnouncement({
                                item: item,
                                index: index
                            })}
                        </View>;
                    }}
                    sliderWidth={windowWidth}
                    itemWidth={windowWidth * 0.85 + spaces.spacingMd}
                    layout={"default"}
                    activeSlideAlignment="start"
                    contentContainerCustomStyle={{
                        paddingLeft: spaces.spacingMd,
                        paddingRight: spaces.spacingMd
                    }}
                    inactiveSlideScale={1}
                    inactiveSlideOpacity={1}
                    onSnapToItem={(index) => setActiveIndex(index)}
                />
                <Pagination
                    dotsLength={announcementData.length}
                    activeDotIndex={activeIndex}
                    containerStyle={stylesheet.paginationContainer}
                    dotStyle={[
                        stylesheet.paginationDot,
                        {
                            backgroundColor: colors.project.gold
                        }
                    ]}
                    inactiveDotStyle={[
                        stylesheet.paginationInactiveDot,
                        {
                            backgroundColor: colors.project.brandInactive
                        }
                    ]}
                    inactiveDotOpacity={1}
                    inactiveDotScale={0.6}
                />
            </View>
            :
            null;
    };

    const renderCategories = () => {
        return <ScrollView
            horizontal={true}
            showsHorizontalScrollIndicator={false}
            ref={categoriesScrollRef}
            style={{
                marginBottom: spaces.spacingLg
            }}
        >
            {newsCategoriesData.map((item, index) => {
                const isSelected = selected === item.key;
                const isLastItem = index === newsCategoriesData.length - 1;

                return <View
                    key={item._id}
                    style={stylesheet.categoryItemContainer}
                >
                    <TouchableOpacity
                        onPress={() => setSelected(item.key as keyof NCoreUIKit.Translation)}
                        style={[
                            stylesheet.categoryButton,
                            {
                                backgroundColor: isSelected ? colors.project.secondary : "transparent",
                                paddingHorizontal: spaces.spacingLg,
                                paddingVertical: spaces.spacingMd,
                                borderRadius: radiuses.full,
                                marginRight: isLastItem ? 0 : spaces.spacingMd
                            }
                        ]}
                    >
                        <Text
                            variant={isSelected ? "titleMediumSize" : "bodyMediumSize"}
                            color={isSelected ? "onPrimary" : "mid"}
                        >
                            {localize(item.titleKey)}
                        </Text>
                    </TouchableOpacity>
                    {!isLastItem && (
                        <View
                            style={[
                                stylesheet.categorySeparator,
                                stylesheet.categorySeparatorLine,
                                {
                                    backgroundColor: colors.content.border.disabled,
                                    marginRight: spaces.spacingMd,
                                    height: spaces.spacingLg
                                }
                            ]}
                        />)}
                </View>;
            })}
        </ScrollView>;
    };

    const renderCompanies = () => {
        return companyData && companyData.length ?
            <View
                style={[
                    stylesheet.companiesContainer,
                    {
                        paddingHorizontal: spaces.spacingMd,
                        paddingVertical: spaces.spacingMd,
                        width: windowWidth
                    }
                ]}
            >
                <View
                    style={stylesheet.sectionHeaderContainer}
                >
                    <Text
                        color="high"
                        variant="titleLargeSize"
                    >
                        {localize("member-companies")}
                    </Text>
                    <TouchableOpacity
                        onPress={() => {
                            navigation.navigate("MainTabs", {
                                screen: "SearchTab",
                                params: {
                                    searchTextParam: undefined,
                                    searchTypeParam: "company"
                                }
                            });
                        }}
                        style={stylesheet.allButtonContainer}
                    >
                        <Text
                            variant="bodyMediumSize"
                            color="mid"
                        >
                            {localize("all")}
                        </Text>
                    </TouchableOpacity>
                </View>
                <ScrollView
                    horizontal={true}
                    showsHorizontalScrollIndicator={false}
                    ref={companiesScrollRef}
                >
                    {companyData && companyData.length ? companyData.map((companyItem) => {
                        return renderItemCompany({
                            item: companyItem
                        });
                    }) : <View
                        style={stylesheet.emptyContentContainer}
                    >
                        <Text
                            color="mid"
                            variant="bodyMediumSize"
                        >
                            {localize("not-found")}
                        </Text>
                    </View>}
                </ScrollView>
            </View>
            :
            null;
    };

    const renderNews = () => {
        return <View
            style={[
                stylesheet.flex1,
                stylesheet.fullWidthContainer,
                {
                    paddingHorizontal: spaces.spacingMd,
                    marginTop: spaces.spacingXl,
                    marginBottom: spaces.spacingXl * 3,
                    width: windowWidth
                }
            ]}
        >
            <View
                style={[
                    stylesheet.sectionHeaderContainer,
                    {
                        marginBottom: spaces.spacingMd
                    }
                ]}
            >
                <Text
                    color="high"
                    variant="titleLargeSize"
                >
                    {localize("news")}
                </Text>
                <TouchableOpacity
                    style={stylesheet.allButtonContainer}
                    onPress={() => {
                        navigation.navigate("News");
                    }}
                >
                    <Text
                        variant="bodyMediumSize"
                        color="mid"
                    >
                        {localize("all")}
                    </Text>
                </TouchableOpacity>
            </View>
            {renderCategories()}
            <ScrollView
                showsHorizontalScrollIndicator={false}
                showsVerticalScrollIndicator={false}
            >
                {newsData.length ? newsData.map((newsItem) => {
                    return renderItemNews({
                        item: newsItem
                    });
                }) : <View
                    style={[
                        stylesheet.emptyContentContainer,
                        stylesheet.emptyNewsContainer,
                        {
                            backgroundColor: colors.content.container.subtle,
                            paddingVertical: spaces.spacingXl * 2,
                            paddingHorizontal: spaces.spacingXl,
                            marginTop: spaces.spacingMd,
                            borderRadius: radiuses.xl
                        }
                    ]}
                >
                    <NewspaperIcon
                        color={colors.content.icon.mid}
                        size={48}
                    />
                    <Text
                        variant="titleLargeSize"
                        color="high"
                        style={[
                            stylesheet.emptyNewsText,
                            {
                                marginTop: spaces.spacingLg
                            }
                        ]}
                    >
                        {localize("not-found") || "Haber Bulunamadı"}
                    </Text>
                    <Text
                        variant="bodyMediumSize"
                        color="mid"
                        style={[
                            stylesheet.emptyNewsText,
                            {
                                marginTop: spaces.spacingSm
                            }
                        ]}
                    >
                        {"Bu kategoride henüz haber bulunmuyor."}
                    </Text>
                </View>
                }
            </ScrollView>
        </View>;
    };

    const renderMenuSheet= () => {
        return <BottomSheet
            ref={bottomSheetRef}
            renderHeader={() => {
                return <View
                    style={[
                        stylesheet.sheetHeaderContainer,
                        {
                            padding: spaces.spacingLg
                        }
                    ]}
                >
                    <Text
                        variant="headlineSmallSize"
                        color="high"
                    >
                        Genç MÜSİAD
                    </Text>
                </View>;
            }}
            isCanFullScreenOnSwipe={false}
            style={{
                borderTopRightRadius: radiuses.xl,
                borderTopLeftRadius: radiuses.xl
            }}
        >
            {menuItems.map((item) => <RowCard
                titleVariant="titleMediumSize"
                key={item.id}
                title={localize(item.titleKey)}
                isTransparentBackground={true}
                style={{
                    marginBottom: spaces.spacingMd
                }}
                icon={() => {
                    const IconComponent = item.icon;
                    return <View
                        style={{
                            marginRight: spaces.spacingMd
                        }}
                    >
                        <IconComponent
                            color={colors.content.icon.onPrimary}
                            size={24}
                        />
                    </View>;
                }}
                rightIcon={() => <ChevronRightIcon
                    color={colors.content.icon.high}
                />
                }
                onPress={() => {
                    if (item.routeName) {
                        bottomSheetRef.current?.close();
                        navigation.navigate(item.routeName as never);
                    }
                }}
            />)}
            <FooterVersionInfo
                isProfile= {false}
            />
        </BottomSheet>;
    };

    if(loading) {
        return <PageContainer
            style={stylesheet.loadingContainer}
        >
            <ActivityIndicator
                size={40}
                color={colors.content.icon.default}
            />
        </PageContainer>;
    }

    const onRefresh = () => {
        setRefreshing(true);
        Promise.all([
            getUnreadNotificationCount(),
            getMyProfile(),
            getNewsCategories(),
            getFeaturedData(),
            getAnnouncements(),
            getCompanies(),
            getNews()
        ]).finally(() => {
            setRefreshing(false);
        });
    };

    return <PageContainer
        isScrollable
        backgroundColor="default"
        isWrapSafeareaContext={true}
        scrollViewProps={{
            showsVerticalScrollIndicator: false,
            refreshControl: <RefreshControl
                refreshing={refreshing}
                onRefresh={onRefresh}
            />
        }}
    >
        <View
            style={stylesheet.bannerContainer}
        >
            <InstallAppBanner />
        </View>
        {renderHeader()}
        {renderSearch()}
        {renderFeatured()}
        {renderAnnouncements()}
        {renderCompanies()}
        {renderNews()}
        {renderMenuSheet()}
    </PageContainer>;
};
export default Home;
